import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, authErrorMessage, upstreamErrorMessage } from '../../lib/api';
import { extractInvoices } from '../../lib/payments';
import type { AttemptResolution, OrphanedCard, PayableInvoice, PaymentAttempt, PropertyCardInfo, SetPropertyCardInput } from '../../types/payments';
import { InvoiceQueue } from '../../components/payments/InvoiceQueue';
import { PayReview } from '../../components/payments/PayReview';
import { PayAllConfirm } from '../../components/payments/PayAllConfirm';
import { RunProgress } from '../../components/payments/RunProgress';
import { RunSummary } from '../../components/payments/RunSummary';
import { WaitingForApproval } from '../../components/payments/WaitingForApproval';
import { AttemptOutcome } from '../../components/payments/AttemptOutcome';
import { PropertyCards } from '../../components/payments/PropertyCards';
import { useAttemptPoller } from '../../hooks/useAttemptPoller';
import { usePaymentRun, type RunHalt } from '../../hooks/usePaymentRun';

type Phase = 'queue' | 'review' | 'waiting' | 'outcome';

function errorMessage(err: unknown, prefix: string): string {
  return err instanceof Error ? `${prefix} (${err.message})` : prefix;
}

/** The run-level explanation under a halted summary (the attempt-specific one is AttemptOutcome's job). */
function runHaltReason(halt: RunHalt): string {
  switch (halt.kind) {
    case 'failed':
      return 'A payment failed. The run halted before initiating anything else; the failed invoice stays in the queue for a deliberate re-attempt.';
    case 'unknown':
      return 'A payment could not be confirmed — the money may already have left the account. The run halted rather than compound that; resolve the attempt below.';
    case 'dry_run':
      return 'The payment service is running in dry-run mode — the portal was walked but nothing was submitted. Continuing would have paid the entire queue without moving any money.';
    case 'initiate-error':
      return `A payment could not be started (${halt.error ?? 'unknown error'}). The run halted before initiating anything else.`;
  }
}

function extractProperties(payload: unknown): Array<{ id: number; name: string }> {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter(
      (row): row is { id: number; name: string } =>
        !!row &&
        typeof row === 'object' &&
        typeof (row as { id?: unknown }).id === 'number' &&
        typeof (row as { name?: unknown }).name === 'string',
    )
    .map((row) => ({ id: row.id, name: row.name }));
}

/**
 * The weekly payment run (CHO-754): work the queue one invoice at a time —
 * review, initiate, wait for the e-banking 2FA approval, see the outcome,
 * move on. CHO-762 adds "pay all pending": a continuous run over the eligible
 * queue — the operator approves N times on the phone but touches the computer
 * once. Every payment is still initiated individually, approved individually,
 * and recorded individually. Still no unattended payment, no batch
 * submission, no select-all, no auto-retry anywhere: nothing moves money
 * without a human approving that specific amount.
 */
export default function PaymentsPage() {
  const [queue, setQueue] = useState<PayableInvoice[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('queue');
  const [selected, setSelected] = useState<PayableInvoice | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<PaymentAttempt | null>(null);
  const [waitingStartedAt, setWaitingStartedAt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [initiateError, setInitiateError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // ── Pay all pending (CHO-762) ────────────────────────────────────────────
  // The run is its own state machine (usePaymentRun); the page wires it to
  // the existing presentational components and owns the queue re-fetch on exit.
  const run = usePaymentRun();
  const runBusy = run.status !== 'idle';
  const eligibleCount = queue.filter((invoice) => invoice.eligibility.payable).length;

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      const data = await api.getPaymentQueue();
      setQueue(extractInvoices(data));
    } catch (err) {
      // CHO-759: payment reads are authenticated like mutations. An auth-class
      // failure (401 session expired, 429 sign-in lockout) must never
      // masquerade as an empty queue or an unreachable service.
      setQueueError(
        authErrorMessage(err) ??
          errorMessage(err, 'The invoice payment service is unreachable — it must be running on the Mac mini'),
      );
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // ── Property cards (CHO-771, payability CHO-799) ─────────────────────────
  // The page owns all fetching: properties from this server, per-property card
  // info from the write-only card API, and the payable-property list from this
  // server. The components below receive data and callbacks only.
  const [properties, setProperties] = useState<Array<{ id: number; name: string }>>([]);
  const [payablePropertyIds, setPayablePropertyIds] = useState<number[]>([]);
  const [cards, setCards] = useState<Record<number, PropertyCardInfo>>({});
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState<string | null>(null);
  const [cardBusy, setCardBusy] = useState<Record<number, boolean>>({});
  const [cardFormError, setCardFormError] = useState<Record<number, string | null>>({});
  const [cardSavedAt, setCardSavedAt] = useState<Record<number, number>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setCardsLoading(true);
      setCardsError(null);
      try {
        const [rawProperties, payable] = await Promise.all([api.getProperties(), api.getPayableProperties()]);
        if (cancelled) return;
        const rows = extractProperties(rawProperties);
        setProperties(rows);
        const payableIdsList = Array.isArray(payable.propertyIds) ? payable.propertyIds : [];
        // Card info is fetched for ALL properties — an excluded property may
        // still hold a UI-set card, which must surface as an orphaned row
        // rather than vanish silently.
        const entries = await Promise.all(
          rows.map(async (property) => [property.id, await api.getPropertyCard(property.id)] as const),
        );
        if (cancelled) return;
        const cardMap = Object.fromEntries(entries);
        setCards(cardMap);
        setPayablePropertyIds(payableIdsList);
      } catch (err) {
        if (cancelled) return;
        // A payable-properties failure fails the whole section — never fall
        // back to rendering every property: that re-opens the non-payable
        // card slot exactly when the filter is down (CHO-799).
        setCardsError(
          authErrorMessage(err) ??
            errorMessage(err, 'Could not load property card configuration from the payment service'),
        );
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const setPropertyCard = useCallback(async (propertyId: number, fields: SetPropertyCardInput) => {
    setCardBusy((map) => ({ ...map, [propertyId]: true }));
    setCardFormError((map) => ({ ...map, [propertyId]: null }));
    try {
      // The response body IS the fresh state — drive the UI from it.
      const info = await api.setPropertyCard(propertyId, fields);
      setCards((map) => ({ ...map, [propertyId]: info }));
      setCardSavedAt((map) => ({ ...map, [propertyId]: Date.now() }));
    } catch (err) {
      setCardFormError((map) => ({
        ...map,
        [propertyId]:
          authErrorMessage(err) ??
          upstreamErrorMessage(err) ??
          errorMessage(err, 'Could not save the card'),
      }));
    } finally {
      setCardBusy((map) => ({ ...map, [propertyId]: false }));
    }
  }, []);

  const deletePropertyCard = useCallback(async (propertyId: number) => {
    setCardBusy((map) => ({ ...map, [propertyId]: true }));
    setCardFormError((map) => ({ ...map, [propertyId]: null }));
    try {
      const info = await api.deletePropertyCard(propertyId);
      setCards((map) => ({ ...map, [propertyId]: info }));
      setCardSavedAt((map) => ({ ...map, [propertyId]: Date.now() }));
    } catch (err) {
      setCardFormError((map) => ({
        ...map,
        [propertyId]:
          authErrorMessage(err) ??
          upstreamErrorMessage(err) ??
          errorMessage(err, 'Could not delete the card'),
      }));
    } finally {
      setCardBusy((map) => ({ ...map, [propertyId]: false }));
    }
  }, []);

  const { attempt: polled, pollError } = useAttemptPoller(phase === 'waiting' ? attemptId : null);
  const latestAttempt = polled ?? attempt;

  // Terminal transition: leave the waiting state exactly when the service
  // reports a terminal status, and never treat an unrecognised status as
  // finished. ('initiated' is the only in-flight status the service has;
  // dry_run is terminal and handled as its own outcome, never as unknown.)
  useEffect(() => {
    if (phase === 'waiting' && latestAttempt && latestAttempt.status !== 'initiated') {
      setAttempt(latestAttempt);
      setPhase('outcome');
    }
  }, [phase, latestAttempt]);

  const startReview = (invoice: PayableInvoice) => {
    setBanner(null);
    setSelected(invoice);
    setAttempt(null);
    setAttemptId(null);
    setInitiateError(null);
    setResolveError(null);
    setPhase('review');
  };

  const backToQueue = useCallback(() => {
    setSelected(null);
    setAttempt(null);
    setAttemptId(null);
    setPhase('queue');
    loadQueue();
  }, [loadQueue]);

  const { reset: resetRun } = run;
  const exitRun = useCallback(() => {
    resetRun();
    loadQueue();
  }, [resetRun, loadQueue]);

  const confirmPay = async () => {
    if (!selected) return;
    setSubmitting(true);
    setInitiateError(null);
    try {
      // The service answers 202 { attemptId, status } — an acknowledgement,
      // not an attempt row. Seed the attempt from the invoice so the wait
      // panel has something to show; the poller replaces it with the real row
      // (including the card's last four) on its first sweep.
      const started = await api.initiatePayment(selected.id);
      setAttemptId(started.attemptId);
      setAttempt({
        id: started.attemptId,
        invoiceId: selected.id,
        amount: selected.amount,
        currency: selected.currency,
        cardLast4: '',
        status: started.status,
        failureReason: null,
        createdAt: new Date().toISOString(),
        resolvedAt: null,
        resolvedBy: null,
        resolvedNote: null,
      });
      setWaitingStartedAt(Date.now());
      setPhase('waiting');
    } catch (err) {
      setInitiateError(errorMessage(err, 'Could not start the payment'));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveAttemptCore = async (target: PaymentAttempt, resolution: AttemptResolution, note: string, onDone: () => void) => {
    setResolving(true);
    setResolveError(null);
    try {
      await api.resolvePaymentAttempt(target.id, resolution, note);
      setBanner(
        resolution === 'succeeded'
          ? 'Attempt marked as paid — the invoice is recorded as settled.'
          : 'Attempt marked as not paid — the invoice is back in the queue.',
      );
      onDone();
    } catch (err) {
      setResolveError(errorMessage(err, 'Could not mark the attempt resolved'));
    } finally {
      setResolving(false);
    }
  };

  const resolveAttempt = (resolution: AttemptResolution, note: string) => {
    if (attempt) void resolveAttemptCore(attempt, resolution, note, backToQueue);
  };

  const resolveRunHalt = (resolution: AttemptResolution, note: string) => {
    if (run.halt?.attempt) void resolveAttemptCore(run.halt.attempt, resolution, note, exitRun);
  };

  const busy = phase !== 'queue';

  const payableProperties = useMemo(
    () => properties.filter((property) => payablePropertyIds.includes(property.id)),
    [properties, payablePropertyIds],
  );

  // Derived, not snapshotted: deleting an orphaned card returns source "none"
  // and updates `cards`, which removes the row here with no extra state
  // (CHO-799 Rule 5).
  const orphanedCards = useMemo<OrphanedCard[]>(
    () =>
      properties.flatMap((property) => {
        if (payablePropertyIds.includes(property.id)) return [];
        const card = cards[property.id];
        return card?.source === 'ui' ? [{ propertyId: property.id, propertyName: property.name, card }] : [];
      }),
    [properties, payablePropertyIds, cards],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice payments</h1>
          <p className="text-sm text-gray-500 mt-1">
            One invoice at a time, each with your approval in the e-banking app.{' '}
            <Link to="/payments/history" className="text-gray-700 underline underline-offset-2 hover:text-gray-900">
              Attempt history
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={run.beginSnapshot}
            disabled={queueLoading || !!queueError || busy || runBusy || eligibleCount === 0}
            className="px-3 py-2 rounded-md text-sm font-medium bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {run.status === 'snapshotting' ? 'Refreshing…' : 'Pay all pending'}
          </button>
          <button
            type="button"
            onClick={loadQueue}
            disabled={queueLoading || busy || runBusy}
            className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
          >
            {queueLoading ? 'Loading…' : 'Refresh queue'}
          </button>
        </div>
      </div>

      {banner && (
        <p className="mb-4 text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2">{banner}</p>
      )}

      {run.confirmError && (
        <p role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {run.confirmError}
        </p>
      )}

      {queueError && (
        <div role="alert" className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-6 text-center">
          <p className="text-sm font-medium text-red-800">{queueError}</p>
          <p className="text-xs text-red-600 mt-1">
            Nothing can be paid until the service is up — this page renders nothing rather than guessing.
          </p>
        </div>
      )}

      {phase === 'queue' && !queueError && (
        <>
          {run.status === 'confirm' && (
            <PayAllConfirm invoices={run.runSet} skipped={run.skipped} onConfirm={run.confirmRun} onCancel={run.cancelConfirm} />
          )}

          {run.status === 'active' && run.runSet[run.currentIndex] && (
            <RunProgress
              current={run.runSet[run.currentIndex]}
              position={run.currentIndex + 1}
              total={run.runSet.length}
              successes={run.successes}
              totals={run.totals}
              initiating={run.initiating}
              onStop={run.stopRun}
            >
              {!run.initiating && (
                <WaitingForApproval
                  invoice={run.runSet[run.currentIndex]}
                  startedAt={run.waitingStartedAt}
                  cardLast4={run.latestAttempt?.cardLast4 || null}
                  pollError={run.pollError}
                />
              )}
            </RunProgress>
          )}

          {run.status === 'completed' && (
            <RunSummary outcome="completed" successes={run.successes} totals={run.totals} onBack={exitRun} />
          )}

          {run.status === 'stopped' && (
            <RunSummary
              outcome="stopped"
              successes={run.successes}
              totals={run.totals}
              inFlight={run.stoppedInFlight}
              onBack={exitRun}
            />
          )}

          {run.status === 'halted' && run.halt && (
            <>
              <RunSummary
                outcome="halted"
                successes={run.successes}
                totals={run.totals}
                haltReason={runHaltReason(run.halt)}
                onBack={run.halt.attempt ? undefined : exitRun}
              />
              {run.halt.attempt && (
                <div className="mt-4">
                  <AttemptOutcome
                    attempt={run.halt.attempt}
                    invoice={run.halt.invoice}
                    onNext={exitRun}
                    onResolve={resolveRunHalt}
                    resolving={resolving}
                    resolveError={resolveError}
                  />
                </div>
              )}
            </>
          )}

          {(run.status === 'idle' || run.status === 'snapshotting') && (
            <InvoiceQueue invoices={queue} busy={busy || runBusy} onPay={startReview} />
          )}
        </>
      )}

      {phase === 'review' && selected && (
        <PayReview
          invoice={selected}
          submitting={submitting}
          error={initiateError}
          onConfirm={confirmPay}
          onBack={backToQueue}
        />
      )}

      {phase === 'waiting' && selected && attempt && (
        <WaitingForApproval invoice={selected} startedAt={waitingStartedAt} cardLast4={latestAttempt?.cardLast4 || null} pollError={pollError} />
      )}

      {phase === 'outcome' && selected && attempt && (
        <AttemptOutcome
          attempt={attempt}
          invoice={selected}
          onNext={backToQueue}
          onResolve={resolveAttempt}
          resolving={resolving}
          resolveError={resolveError}
        />
      )}

      <PropertyCards
        properties={payableProperties}
        cards={cards}
        orphanedCards={orphanedCards}
        loading={cardsLoading}
        error={cardsError}
        busyByProperty={cardBusy}
        errorByProperty={cardFormError}
        savedAtByProperty={cardSavedAt}
        onSetCard={setPropertyCard}
        onDeleteCard={deletePropertyCard}
      />
    </div>
  );
}
